import { z } from "zod";

import {
  isMcpToolAttachable,
  type McpToolStatusValue,
  type UncallableMcpToolStatusValue
} from "./mcp-tool-attachability";

/**
 * Why an agent skill's pin on an MCP tool will be REFUSED at call time.
 *
 * ## One predicate, two consumers, and that is the whole point of the file
 *
 * Dispatch refuses a broken pin before it dials anything. An administrator has to
 * be able to SEE that the same pin is broken, before an end user finds out by
 * asking an agent to do something it silently cannot do. Those are the same
 * question asked at two moments, and written twice they are free to disagree — in
 * the direction that matters most, where a screen says a skill is fine over a call
 * that refuses it. Nothing goes red when they drift: the read is honest about its
 * own rule, and its rule is simply not the one enforced.
 *
 * So the rule is here, once, and both sides call it. `call-pinned-mcp-tool.use-case.ts`
 * maps each reason to the error it throws; `ListBrokenMcpToolPinsUseCase` maps each
 * to a row an admin reads.
 *
 * ## Two reasons, and the second is invisible to `status` alone
 *
 * - `TOOL_NOT_CALLABLE` — the tool is not APPROVED at all. The four states this
 *   covers are the four `McpToolStatus` holds besides APPROVED, and the tool is
 *   broken for every pin on it.
 * - `PINNED_CONTRACT_SUPERSEDED` — the tool IS approved, at a contract that is not
 *   the one this skill was attached against. A tool approved, changed, then
 *   approved again reads APPROVED throughout, so no status and no server-level
 *   view can see this; only the pin's own hash can. It is per-PIN: two agents on
 *   one tool can differ, one broken and one fine.
 *
 * ## The order is the same order dispatch refuses in, and it has to be
 *
 * The status is tested first. Under `PENDING_APPROVAL` or `REJECTED` the row's
 * `approvedSchemaHash` is NULL, so the hash comparison below would be true for
 * every pin and would report `PINNED_CONTRACT_SUPERSEDED` — a reason whose remedy
 * (re-attach the agent) is wrong, because what that tool actually needs is an
 * administrator's approval. Testing the status first makes the hash comparison
 * reachable only where a hash exists.
 */
export const MCP_PIN_BREAKAGE_REASONS = [
  "TOOL_NOT_CALLABLE",
  "PINNED_CONTRACT_SUPERSEDED"
] as const;

export const McpPinBreakageReasonSchema = z.enum(MCP_PIN_BREAKAGE_REASONS);

export type McpPinBreakageReason = z.infer<typeof McpPinBreakageReasonSchema>;

/**
 * The verdict, carrying the narrowed status on the arm that has one.
 *
 * A discriminated union rather than the bare reason, so the call-time refusal can
 * hand `status` straight to `McpToolNotCallableError` — which takes exactly the
 * uncallable statuses — with no cast and no re-derivation. The published response
 * carries `McpPinBreakageReason` instead: it already returns the tool's status as
 * its own field, and repeating it inside the verdict would be two renderings of one
 * column.
 */
export type McpPinBreakage =
  | { readonly reason: "TOOL_NOT_CALLABLE"; readonly status: UncallableMcpToolStatusValue }
  | { readonly reason: "PINNED_CONTRACT_SUPERSEDED" };

/** The tool columns the verdict is computed from. Nothing else is read. */
export interface McpPinnedToolState {
  readonly status: McpToolStatusValue;
  readonly approvedSchemaHash: string | null;
}

/**
 * Whether this pin is broken, and why — or `null` when the skill may call.
 *
 * `null` for "fine" rather than a `{ ok: true }` member: every caller acts on the
 * broken case and passes the healthy one through, so a nullable verdict is the
 * shape both of them already want.
 */
export function resolveMcpPinBreakage(
  tool: McpPinnedToolState,
  pinnedSchemaHash: string
): McpPinBreakage | null {
  if (!isMcpToolAttachable(tool.status)) {
    return { reason: "TOOL_NOT_CALLABLE", status: tool.status };
  }
  // Reachable only under APPROVED, where `McpServerTool_status_consistent` makes
  // `approvedSchemaHash` NOT NULL — so this compares two hashes rather than
  // silently comparing a hash against null.
  if (tool.approvedSchemaHash !== pinnedSchemaHash) {
    return { reason: "PINNED_CONTRACT_SUPERSEDED" };
  }
  return null;
}
